import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DOWN_ARROW, ENTER, UP_ARROW } from '@angular/cdk/keycodes';
import { KerkimiService } from '@core/services/kerkimi.service';
import { Kerkimi } from '@core/interfaces/models/kerkimi.interface';
import { OrderBy } from '@core/enums/order-by.enum';
import { OrderType } from '@core/enums/order-type.enum';
import { fromWorker } from 'observable-webworker';
import { FilterType } from '@core/enums/filter-type.enum';
import { NavigationService } from '@core/services/navigation.service';
import { EditorService } from '@core/services/editor.service';
import { BazaKomenti } from '@core/interfaces/models/baza-komenti.interface';
import { OperatoriFaqjaService } from '@core/services/operatori-faqja.service';
import { QueryBuilderService } from '@core/services/query-builder.service';
import { ParacaktimiKerkimitService } from '@core/services/paracaktimi-kerkimit.service';
import { MorfologjiaHubService } from '@core/services/signalr/morfologjia-hub.service';
import { cloneDeep } from 'lodash-es';
import { AuthService } from '@core/auth/auth.service';
import { notFoundWindow } from '@core/constants/animations';

@Component({
  selector: 'app-word-list',
  templateUrl: './word-list.component.html',
  styleUrls: ['./word-list.component.scss'],
  animations: [notFoundWindow]
})
export class WordListComponent implements OnInit, AfterViewInit, OnDestroy {
  bazaList: Kerkimi[] = [];

  activeBazaId: number | undefined = undefined;
  lockedBazat: { operatoriName: string; bazaIds: number[] }[] = [];

  countResult: number = 0;
  unfilteredCountResult?: number;
  currentIndex: number = 0;

  loading: boolean = true;
  lazyLoading: boolean = false;
  notFoundInList: boolean = false;
  preserveScroll: boolean = false;

  foundWordIndex: number = -1;

  userId: string | null = null;

  private _scrolling: boolean = false;
  scrollLocked: boolean = false;

  komentetGroup?: { operatoriId?: string; komentet: BazaKomenti[] }[];

  activeFilter$: Observable<FilterType>;
  activeBurimiId$: Observable<string | null>;
  activeOrder$: Observable<OrderBy>;
  activeOrderType$: Observable<OrderType>;

  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  termiKerkimit: string | null = null;

  private _onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private authService: AuthService,
    private queryBuilderService: QueryBuilderService,
    private operatoriFaqjaService: OperatoriFaqjaService,
    private paracaktimiKerkimitService: ParacaktimiKerkimitService,
    private navigationService: NavigationService,
    private kerkimiService: KerkimiService,
    private editorService: EditorService,
    private morfologjiaHubService: MorfologjiaHubService
  ) {
    this.activeFilter$ = operatoriFaqjaService.listenOnFieldChanges('termiFilterit').pipe(startWith(FilterType.AllWords));
    this.activeBurimiId$ = operatoriFaqjaService.listenOnFieldChanges('burimiId').pipe(startWith(null));
    this.activeOrder$ = operatoriFaqjaService.listenOnFieldChanges('renditjaSipas');
    this.activeOrderType$ = operatoriFaqjaService.listenOnFieldChanges('tipiRenditjes');
  }

  ngAfterViewInit(): void {
    this.viewport!.elementScrolled()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        if (!this._scrolling) this.scrollLocked = false;
        else this._scrolling = false;
      });
  }

  ngOnInit(): void {
    this.authService.isDoneLoading$
      .pipe(
        filter((isDone) => isDone),
        switchMap(() => this.authService.isAuthenticated$),
        map((isAuthenticated) => {
          if (isAuthenticated) return this.authService.userId;
          return null;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((userId) => {
        this.userId = userId;
      });

    this.operatoriFaqjaService
      .listenOnFieldChanges('bazaId')
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((bazaId) => {
        this.activeBazaId = bazaId;
        if (this.activeBazaId) document.getElementById('w-' + this.activeBazaId)?.focus();

        this.onActiveBazaAndKerkimiListUpdated();
      });

    combineLatest([this.kerkimiService.getFullWordList(), this.operatoriFaqjaService.listenOnChanges(['renditjaSipas', 'tipiRenditjes'])])
      .pipe(
        filter(([list]) => list != undefined && list.length > 0),
        tap(() => (this.lazyLoading = true)),
        switchMap(([list, operatoriFaqja]) => {
          if (operatoriFaqja.renditjaSipas != OrderBy.Headword || operatoriFaqja.tipiRenditjes != OrderType.Ascending)
            return fromWorker<[Kerkimi[], OrderBy, OrderType], Kerkimi[]>(
              () =>
                new Worker(new URL('./web-workers/baza-sort.worker', import.meta.url), {
                  type: 'module',
                  name: 'baza-sort',
                }),
              of([list!, OrderBy.Headword, OrderType.Ascending])
            );
          return of(list!);
        }),
        switchMap((list) => {
          return combineLatest([
            of(list),
            this.operatoriFaqjaService.listenOnChanges(['termiFilterit', 'burimiId']),
            this.operatoriFaqjaService.listenOnFieldChanges('termiKerkimit').pipe(debounceTime(275), distinctUntilChanged()),
          ]);
        }),
        tap(([list, op, termiKerkimit]) => {
          this.lazyLoading = true;
          this.notFoundInList = false;
          this.foundWordIndex = -1;
          this.termiKerkimit = termiKerkimit;
        }),
        switchMap(([list, operatoriFaqja, termiKerkimit]) => {
          if ((termiKerkimit != null && termiKerkimit != '') || (operatoriFaqja.burimiId && operatoriFaqja.burimiId != '')) {
            this.lazyLoading = true;
            this.unfilteredCountResult = list.length;

            if (operatoriFaqja.termiFilterit != FilterType.AllWords && operatoriFaqja.termiFilterit != FilterType.ById) {
              return combineLatest([
                fromWorker<[Kerkimi[], FilterType, string, string], Kerkimi[]>(
                  () =>
                    new Worker(new URL('./web-workers/baza-search.worker', import.meta.url), {
                      type: 'module',
                      name: 'baza-search',
                    }),
                  of([list, operatoriFaqja.termiFilterit, termiKerkimit, operatoriFaqja.burimiId])
                ),
                of(null),
              ]);
            } else {
              return fromWorker<[Kerkimi[], FilterType, string, string], [Kerkimi[], number]>(
                () =>
                  new Worker(new URL('./web-workers/baza-find.worker', import.meta.url), {
                    type: 'module',
                    name: 'baza-find',
                  }),
                of([list, operatoriFaqja.termiFilterit, termiKerkimit, operatoriFaqja.burimiId])
              );
            }
          }
          this.unfilteredCountResult = undefined;
          return combineLatest([of(list), of(null)]);
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([list, foundIndex]) => {
        this.bazaList = list;

        this.onActiveBazaAndKerkimiListUpdated();

        if (foundIndex != null && this.termiKerkimit) {
          if (foundIndex != -1) {
            setTimeout(() => {
              this.scrollToWord(foundIndex);
              this.foundWordIndex = foundIndex;
            }, 0);
          } else {
            this.notFoundInList = true;
          }
        }
        this.lazyLoading = false;
      });

    combineLatest([
      this.operatoriFaqjaService.listenOnFieldChanges('paracaktimiKerkimitId').pipe(
        switchMap((paracaktimiKerkimitId) => {
          return this.paracaktimiKerkimitService.getFromStore().pipe(
            map((paracaktimiKerkimit) => {
              return paracaktimiKerkimit.find((x) => x.id == paracaktimiKerkimitId)?.objektiKerkimit;
            }),
            distinctUntilChanged(),
            map((objektiKerkimit) => {
              return objektiKerkimit ? JSON.parse(objektiKerkimit) : null;
            })
          );
        })
      ),
      this.queryBuilderService.getTemporaryQueryBuilder().pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))),
    ])
      .pipe(
        tap(() => {
          this.loading = true;
          this.notFoundInList = false;
          this.bazaList = [];
          this.onActiveBazaAndKerkimiListUpdated();
          this.kerkimiService.resetFullWordList();
        }),
        switchMap(([paracaktimiKerkimit, temporaryQuery]) => {
          const query = paracaktimiKerkimit || temporaryQuery;
          if (query) {
            return this.queryBuilderService.get(query);
          }
          return this.kerkimiService.get();
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list);
        this.loading = false;
      });

    this.navigationService
      .onNextClick()
      .pipe(
        withLatestFrom(this.navigationService.getNextFjala()),
        filter(([, fjalaId]) => fjalaId != null),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([, fjalaId]) => {
        this.tryActivateBaza(fjalaId!);
      });

    this.navigationService
      .onPrevClick()
      .pipe(
        withLatestFrom(this.navigationService.getPrevFjala()),
        filter(([, fjalaId]) => fjalaId != null),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([, fjalaId]) => {
        this.tryActivateBaza(fjalaId!);
      });

    this.navigationService
      .onEnterClick()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        if (this.foundWordIndex) {
          const id = this.bazaList[this.foundWordIndex]?.id;
          if (id) this.tryActivateBaza(id);
          this.scrollToWord(this.foundWordIndex);
        }
      });

    this.subscribeToHubEvents();
  }

  trackById(index: number, item: any) {
    return item.id;
  }

  keyPress(event: KeyboardEvent, kerkimiId: number) {
    if (event.keyCode == ENTER) {
      this.operatoriFaqjaService.updateItems({ bazaId: kerkimiId });
    }
    event.preventDefault();
  }

  keyDown(event: KeyboardEvent) {
    if (event.keyCode == DOWN_ARROW) {
      this.navigationService.triggerNextClick();
    } else if (event.keyCode == UP_ARROW) {
      this.navigationService.triggerPrevClick();
    }
    event.preventDefault();
  }

  tryActivateBaza(id: number, preserveScrollLock: boolean = true) {
    if (id != this.activeBazaId) {
      this.editorService
        .getSaving()
        .pipe(
          take(1),
          filter((saving) => !saving),
          switchMap(() => this.editorService.getCanChangeWord())
        )
        .subscribe((canChange) => {
          if (canChange) {
            if (!preserveScrollLock) this.scrollLocked = false;
            this.operatoriFaqjaService.updateItems({ bazaId: id });
          }
        });
    }
  }

  scrollToWord(index: number) {
    if (this.viewport && index > -1) {
      let offset = index * 35 - this.viewport.getViewportSize() / 2 + 18;
      offset = offset < 0 ? 0 : offset;
      this._scrolling = true;
      this.viewport.scrollToOffset(offset);
      this.scrollLocked = true;
    }
  }

  // isLocked(wordID: number) {}

  private subscribeToHubEvents() {
    this.morfologjiaHubService
      .getLockedBazat()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((lockedBazat) => {
        this.lockedBazat = lockedBazat;
      });

    this.morfologjiaHubService
      .onBazaInserted()
      .pipe(
        withLatestFrom(this.kerkimiService.getFullWordList()),
        filter(([kerkimet, list]) => list != undefined),
        tap(() => {
          // this.preserveScroll = true;
        }),
        map(([kerkimet, list]) => {
          let theList = cloneDeep(list!);
          for (let i = 0; i < kerkimet.length; i++) theList = this.insertKerkimiIntoList(kerkimet[i], theList);
          return theList;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list!);
      });

    this.morfologjiaHubService
      .onBazaUpdated()
      .pipe(
        withLatestFrom(this.kerkimiService.getFullWordList()),
        filter(([kerkimet, list]) => list != undefined),
        tap(() => {
          // this.preserveScroll = true;
        }),
        map(([kerkimet, list]) => {
          let theList = cloneDeep(list!);

          for (let i = 0; i < kerkimet.length; i++) {
            let index = theList.findIndex((c) => c.id == kerkimet[i].id);
            if (index != -1) {
              theList.splice(index, 1);
              theList = this.insertKerkimiIntoList(kerkimet[i], theList);
            }
          }

          return theList;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list!);
      });

    this.morfologjiaHubService
      .onBazaDeleted()
      .pipe(
        withLatestFrom(this.kerkimiService.getFullWordList()),
        filter(([bazaIds, list]) => list != undefined && list.some((i) => bazaIds.some((b) => b == i.id))),
        map(([bazaIds, list]) => {
          let bazaIdsFiltered = bazaIds.filter((b) => list!.some((l) => l.id == b));
          let theList = cloneDeep(list!);

          for (let i = 0; i < bazaIdsFiltered.length; i++) {
            let index = theList.findIndex((e) => e.id == bazaIdsFiltered[i]);
            theList.splice(index, 1);
          }
          return theList;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list!);
      });

    this.morfologjiaHubService
      .onBazaKomentiInserted()
      .pipe(
        withLatestFrom(this.kerkimiService.getFullWordList()),
        filter(([komenti, list]) => list != undefined && list.some((i) => i.id == komenti.bazaId)),
        map(([komenti, list]) => {
          let theList = cloneDeep(list!);
          let index = theList.findIndex((i) => i.id == komenti.bazaId);
          theList[index].nrKomenteve = (theList[index].nrKomenteve ?? 0) + 1;
          return theList;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list!);
      });

    this.morfologjiaHubService
      .onBazaKomentiDeleted()
      .pipe(
        withLatestFrom(this.kerkimiService.getFullWordList()),
        filter(([komenti, list]) => list != undefined && list.some((i) => i.id == komenti.bazaId)),
        map(([komenti, list]) => {
          let theList = cloneDeep(list!);
          let index = theList.findIndex((i) => i.id == komenti.bazaId);
          theList[index].nrKomenteve = (theList[index].nrKomenteve ?? 1) - 1;
          return theList;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((list) => {
        this.kerkimiService.setFullWordList(list!);
      });
  }

  private insertKerkimiIntoList(kerkimi: Kerkimi, list: Kerkimi[]) {
    if (list.length == 0) list.push(kerkimi);
    else {
      let addKerkimi = false;

      const b = kerkimi.fjala.toUpperCase();
      for (let i = 0, len = list.length; i < len; i++) {
        const a = list[i].fjala.toUpperCase();

        if (a.albanianCompare(b) >= 0) {
          if (a.albanianCompare(b) == 0) {
            if (kerkimi.pjesaLigjeratesId <= list[i].pjesaLigjeratesId) {
              if (kerkimi.pjesaLigjeratesId == list[i].pjesaLigjeratesId) {
                const bGjinia = kerkimi.gjiniaId ?? 0;
                const aGjinia = list[i].gjiniaId ?? 0;

                if (bGjinia <= aGjinia) {
                  if (bGjinia == aGjinia) {
                    const bLloji = kerkimi.llojiId ?? 0;
                    const aLloji = list[i].llojiId ?? 0;

                    if (bLloji <= aLloji) {
                      if (bLloji == aLloji) {
                        if (kerkimi.id < list[i].id) {
                          addKerkimi = true;
                        }
                      } else {
                        addKerkimi = true;
                      }
                    }
                  } else {
                    addKerkimi = true;
                  }
                }
              } else {
                addKerkimi = true;
              }
            }
          } else {
            addKerkimi = true;
          }
        }
        if (addKerkimi) {
          list.splice(i, 0, kerkimi);
          break;
        }
      }

      if (!addKerkimi) list.push(kerkimi);
    }
    return list;
  }

  private onActiveBazaAndKerkimiListUpdated() {
    if (this.activeBazaId && this.bazaList && this.bazaList.length > 0) {
      let index = this.bazaList.findIndex((w) => w.id == this.activeBazaId);

      this.currentIndex = index; // index == -1 ? 0 : index + 1;

      if (index > 0) {
        this.navigationService.setPrevFjala(this.bazaList[index - 1].id);
      } else {
        this.navigationService.setPrevFjala(null);
      }

      if (index < this.bazaList.length - 1) {
        this.navigationService.setNextFjala(this.bazaList[index + 1].id);
      } else {
        this.navigationService.setNextFjala(null);
      }

      if (index == -1) {
        this.scrollLocked = false;
      }

      if (this.scrollLocked) {
        setTimeout(() => {
          this.scrollToWord(this.currentIndex);
        }, 0);
      }
    } else {
      this.currentIndex = -1;
      // this.scrollLocked = false;

      this.navigationService.setPrevFjala(null);
      this.navigationService.setNextFjala(null);
    }

    this.countResult = this.bazaList.length;
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
    this.kerkimiService.resetFullWordList();
  }
}
